// Imam Carpets shortlist. Saves rugs in this browser (no account, no app), shows
// them in a drawer, shares them as a link (?shortlist=handle,handle) and sends
// them on WhatsApp to ask for a home trial (Karachi) or a video viewing.
// Rug details are fetched only when the drawer opens, so page load stays light.

const KEY = 'imam-shortlist';
const MAX = 30;
const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

const config = (() => {
  try {
    return JSON.parse(document.getElementById('ImamShortlistConfig')?.textContent || '{}');
  } catch {
    return {};
  }
})();

const esc = (value) =>
  String(value).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

const cleanHandles = (list) =>
  list.filter((h) => typeof h === 'string' && /^[a-z0-9][a-z0-9-]*$/i.test(h)).slice(0, MAX);

/* Storage */

function read() {
  try {
    const list = JSON.parse(localStorage.getItem(KEY) || '[]');
    return Array.isArray(list) ? cleanHandles(list) : [];
  } catch {
    return [];
  }
}

function write(list) {
  try {
    localStorage.setItem(KEY, JSON.stringify(cleanHandles(list)));
  } catch {}
  update();
}

const has = (handle) => read().includes(handle);

function add(handle) {
  const list = read();
  if (!list.includes(handle)) write([...list, handle]);
}

function remove(handle) {
  write(read().filter((h) => h !== handle));
}

/* Header count and product page buttons */

function update() {
  const count = read().length;

  document.querySelectorAll('[data-imam-shortlist-count]').forEach((el) => {
    el.textContent = count;
    el.closest('.cart-link__bubble')?.classList.toggle('cart-link__bubble--visible', count > 0);
  });

  // On phones the header icon only shows once the shortlist holds rugs
  document.querySelectorAll('.imam-sl-toggle').forEach((el) => el.classList.toggle('has-items', count > 0));

  document.querySelectorAll('[data-imam-shortlist-menu-count]').forEach((el) => {
    el.textContent = count > 0 ? `(${count})` : '';
  });

  document.querySelectorAll('[data-imam-shortlist-add]').forEach((btn) => {
    const saved = has(btn.dataset.imamShortlistAdd);
    btn.classList.toggle('is-saved', saved);
    btn.setAttribute('aria-pressed', String(saved));
    const label = btn.querySelector('[data-label]');
    // Product cards are narrow: their button says "Shortlisted" once saved
    const savedText = btn.hasAttribute('data-imam-shortlist-compact') ? 'Shortlisted' : 'Saved · view shortlist';
    if (label) label.textContent = saved ? savedText : 'Add to shortlist';
  });
}

/* Rug details */

const cache = new Map();

// From sections/imam-rug-data.liquid rather than /products/<handle>.js: the rug's name depends on
// custom.manufacturer, which the .js data leaves out. The section holds the rug's data as JSON and
// its name block (the same one as the product cards), which the drawer shows as it is.
function getProduct(handle) {
  if (!cache.has(handle)) {
    cache.set(
      handle,
      fetch(`${root}products/${encodeURIComponent(handle)}?section_id=imam-rug-data`)
        .then((r) => (r.ok ? r.text() : null))
        .then((html) => {
          if (!html) return null;
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const data = JSON.parse(doc.querySelector('[data-imam-rug]')?.textContent || 'null');
          if (!data || !data.handle) return null;
          data.nameHtml = doc.querySelector('.imam-card-name')?.outerHTML || '';
          return data;
        })
        .catch(() => null)
    );
  }
  return cache.get(handle);
}

// The full title cut at " - Size", for the WhatsApp message and image alt text:
// "Florea Hand Knotted Rug - Size: 10.3 x 8.3" -> name + 10′ 3″ × 8′ 3″
function parseTitle(title) {
  const name = title.split(' - Size')[0].trim();
  let size = '';
  const dims = (title.split('Size:')[1] || '').trim().split(' x ');
  if (dims.length === 2) {
    size = dims
      .map((dim) => {
        const [ft, inch] = dim.trim().split('.');
        const f = parseInt(ft, 10) || 0;
        const i = parseInt(inch, 10) || 0;
        return i > 0 ? `${f}′ ${i}″` : `${f}′`;
      })
      .join(' × ');
  }
  return { name, size };
}

const money = (cents) => `Rs.${Math.round(cents / 100).toLocaleString('en-US')}`;

/* Drawer */

let dialog;
let mode = 'own'; // 'own' = this browser's list, 'shared' = a list opened from a link
let sharedHandles = [];
let items = [];
let renderToken = 0;

const currentHandles = () => (mode === 'shared' ? sharedHandles : read());

// Links always use the store's main domain (imamcarpets.com), also in theme previews.
function shareUrl() {
  const origin = config.domain ? `https://${config.domain}` : location.origin;
  return `${origin}${root}?shortlist=${currentHandles().map(encodeURIComponent).join(',')}`;
}

function whatsappUrl(kind) {
  const lines = items
    .filter((item) => item.product)
    .map((item, n) => {
      const { name, size } = parseTitle(item.product.title);
      const price = item.product.available ? `, ${money(item.product.price)}` : ' (sold)';
      return `${n + 1}. ${name}${size ? `, ${size}` : ''}${price}`;
    });

  const intro =
    kind === 'trial'
      ? "Hi Imam Carpet Co., I'd like to request a home trial in Karachi for these rugs:"
      : "Hi Imam Carpet Co., I'd like a video viewing of these rugs:";
  const ask = kind === 'trial' ? 'My area:' : 'My city:';
  const text = [intro, ...lines, '', `My shortlist: ${shareUrl()}`, ask].join('\n');

  return `https://wa.me/${config.whatsapp || ''}?text=${encodeURIComponent(text)}`;
}

function itemHtml({ handle, product }) {
  const removeButton =
    mode === 'own' ? `<button type="button" class="imam-sl__remove" data-remove="${esc(handle)}">Remove</button>` : '';

  if (!product) {
    return `<div class="imam-sl__item"><span></span><div class="imam-sl__info">
      <p class="imam-sl__name">No longer available</p>${removeButton}</div></div>`;
  }

  const { name } = parseTitle(product.title);
  const url = product.url || `${root}products/${product.handle}`;
  const image = product.image
    ? `<img src="${esc(product.image)}" alt="${esc(name)}" width="88" height="110" loading="lazy">`
    : '';

  // Name, rest of the title and size come as one block from Liquid (snippets/imam.card-name.liquid)
  return `<div class="imam-sl__item">
    <a class="imam-sl__img" href="${esc(url)}">${image}</a>
    <div class="imam-sl__info">
      ${product.nameHtml || `<a class="imam-sl__name" href="${esc(url)}">${esc(name)}</a>`}
      <p class="imam-sl__price${product.available ? '' : ' is-sold'}">${product.available ? esc(money(product.price)) : 'Sold'}</p>
      ${removeButton}
    </div>
  </div>`;
}

function buildDialog() {
  dialog = document.createElement('dialog');
  dialog.className = 'imam-sl';
  dialog.setAttribute('aria-labelledby', 'ImamShortlistTitle');
  dialog.innerHTML = `
    <div class="imam-sl__panel">
      <div class="imam-sl__head">
        <h2 class="imam-sl__eyebrow" id="ImamShortlistTitle">
          <span data-eyebrow>Your shortlist</span><span class="imam-sl__count" data-count></span>
        </h2>
        <button type="button" class="imam-sl__close" data-close aria-label="Close shortlist">
          <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true"><path d="M5 5l14 14M19 5L5 19" stroke="currentColor" stroke-width="1.5" fill="none"/></svg>
        </button>
      </div>
      <div class="imam-sl__list" data-list></div>
      <div class="imam-sl__foot" data-foot>
        <button type="button" class="imam-sl__btn" data-save hidden>Save to my shortlist</button>
        <button type="button" class="imam-sl__btn imam-sl__btn--primary" data-cart>Add all to cart</button>
        <p class="imam-sl__label">See them before you buy</p>
        <div class="imam-sl__row">
          <a class="imam-sl__btn imam-sl__btn--small" data-whatsapp="trial" href="#" target="_blank" rel="noopener">
            <span>Home trial</span><small>Karachi, on request</small>
          </a>
          <a class="imam-sl__btn imam-sl__btn--small" data-whatsapp="video" href="#" target="_blank" rel="noopener">
            <span>Video viewing</span><small>Live showroom tour</small>
          </a>
        </div>
        <button type="button" class="imam-sl__link" data-copy>Copy link to share</button>
        <input class="imam-sl__field" data-copy-field type="text" readonly hidden aria-label="Shortlist link">
      </div>
    </div>`;
  document.body.appendChild(dialog);

  dialog.addEventListener('click', (event) => {
    if (event.target === dialog) return closeDrawer(); // backdrop

    const target = event.target.closest('button, a');
    if (!target) return;

    if (target.matches('[data-close]')) closeDrawer();
    if (target.matches('[data-remove]')) {
      remove(target.dataset.remove);
      render();
    }
    if (target.matches('[data-cart]')) addAllToCart(target);
    if (target.matches('[data-copy]')) copyLink(target);
    if (target.matches('[data-save]')) {
      write([...read(), ...sharedHandles.filter((h) => !read().includes(h))]);
      mode = 'own';
      render();
    }
    if (target.matches('[data-whatsapp]')) target.href = whatsappUrl(target.dataset.whatsapp);
  });

  dialog.addEventListener('close', () => document.documentElement.classList.remove('imam-sl-open'));
}

async function render() {
  const token = ++renderToken;
  const handles = currentHandles();
  const shared = mode === 'shared';

  dialog.querySelector('[data-eyebrow]').textContent = shared ? 'Shared with you' : 'Your shortlist';
  dialog.querySelector('[data-save]').hidden = !shared;
  dialog.querySelector('[data-count]').textContent = handles.length
    ? `· ${handles.length} ${handles.length === 1 ? 'rug' : 'rugs'}`
    : '';
  dialog.querySelector('[data-foot]').hidden = handles.length === 0;
  dialog.querySelector('[data-copy-field]').hidden = true;

  const list = dialog.querySelector('[data-list]');
  if (!handles.length) {
    items = [];
    list.innerHTML =
      '<p class="imam-sl__empty">Nothing saved yet. Tap “Add to shortlist” on any rug and it will wait here.</p>';
    return;
  }

  list.innerHTML = handles.map(() => '<div class="imam-sl__item is-loading"></div>').join('');
  const products = await Promise.all(handles.map(getProduct));
  if (token !== renderToken) return;

  items = handles.map((handle, i) => ({ handle, product: products[i] }));
  list.innerHTML = items.map(itemHtml).join('');

  const cart = dialog.querySelector('[data-cart]');
  cart.disabled = !items.some((item) => item.product && item.product.available);
  cart.textContent = 'Add all to cart';

  dialog.querySelectorAll('[data-whatsapp]').forEach((link) => {
    link.href = whatsappUrl(link.dataset.whatsapp);
  });
}

function openDrawer(nextMode = 'own') {
  mode = nextMode;
  if (!dialog) buildDialog();
  if (!dialog.open) {
    if (typeof dialog.showModal === 'function') dialog.showModal();
    else dialog.setAttribute('open', '');
    document.documentElement.classList.add('imam-sl-open');
  }
  render();
}

function closeDrawer() {
  if (!dialog) return;
  if (typeof dialog.close === 'function') dialog.close();
  else dialog.removeAttribute('open');
  document.documentElement.classList.remove('imam-sl-open');
}

async function addAllToCart(button) {
  const ids = items
    .filter((item) => item.product && item.product.available)
    .map((item) => (item.product.variants.find((v) => v.available) || {}).id)
    .filter(Boolean);
  if (!ids.length) return;

  button.disabled = true;
  button.textContent = 'Adding…';

  // One at a time, so a rug already in the cart does not stop the others.
  for (const id of ids) {
    try {
      await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id, quantity: 1 }] }),
      });
    } catch {}
  }
  location.href = `${root}cart`;
}

async function copyLink(button) {
  const url = shareUrl();
  try {
    await navigator.clipboard.writeText(url);
    button.textContent = 'Link copied';
    setTimeout(() => (button.textContent = 'Copy link to share'), 2000);
  } catch {
    const field = dialog.querySelector('[data-copy-field]');
    field.value = url;
    field.hidden = false;
    field.select();
  }
}

/* Start */

document.addEventListener('click', (event) => {
  const open = event.target.closest('[data-imam-shortlist-open]');
  if (open) {
    event.preventDefault();
    openDrawer('own');
    return;
  }

  const addButton = event.target.closest('[data-imam-shortlist-add]');
  if (addButton) {
    event.preventDefault();
    const handle = addButton.dataset.imamShortlistAdd;
    if (has(handle)) openDrawer('own');
    else add(handle);
  }
});

window.addEventListener('storage', (event) => {
  if (event.key === KEY) update();
});

// Cards that arrive later (filtering, recommendations, quick-shop) get their saved state too
document.addEventListener('collection:reloaded', update);
let pendingUpdate = 0;
new MutationObserver((records) => {
  const added = records.some((r) => [...r.addedNodes].some((n) => n.nodeType === 1 && (n.matches?.('[data-imam-shortlist-add]') || n.querySelector?.('[data-imam-shortlist-add]'))));
  if (added && !pendingUpdate) pendingUpdate = requestAnimationFrame(() => { pendingUpdate = 0; update(); });
}).observe(document.body, { childList: true, subtree: true });

update();

const sharedParam = new URLSearchParams(location.search).get('shortlist');
if (sharedParam) {
  sharedHandles = cleanHandles(sharedParam.split(',').map((h) => h.trim()));
  if (sharedHandles.length) openDrawer('shared');
}
